import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaArrowRight, FaCheck, FaTimes,
  FaHandshake, FaCalendarAlt,
  FaUsers, FaCheckCircle,
  FaBoxOpen, FaPen, FaUserPlus, FaSms, FaGift, FaFileInvoice, FaCreditCard,
  FaCalendar, FaClock, FaIdCard,
} from 'react-icons/fa';
import { Toaster } from 'react-hot-toast';
import { useProContext } from '../context/ProContext';
import ProLayout from '../layout/ProLayout';
import NotificationCenter from '../components/NotificationCenter';
import MessageDrawer from '../components/MessageDrawer';
import { showToast } from '../utils/toast';
import api from '../../../services/api';

const DashboardPage = () => {
  const {
    currentUser,
    brand, brandName, brandLogo, isAA,
    clients, ongoing, completed,
    messages, unreadMessages, fetchMessages, sendMessage, markAsRead,
    stats, statsLoading: sL,
    sponsors, fetchSponsors,
    vouchers,
    isOptionActive,
    rdvList,
  } = useProContext();
  const navigate = useNavigate();

  const [selectedClient, setSelectedClient] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [togglingCardAdv, setTogglingCardAdv] = useState(new Set());

  const pendingCount = ongoing.filter(c => c.status === 'pending').length;

  // Today's recommendations
  const todayRecos = useMemo(() => {
    const today = new Date().toDateString();
    return clients.filter(c => new Date(c.created_at).toDateString() === today)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [clients]);

  // Today's new users
  const todayUsers = useMemo(() => {
    const today = new Date().toDateString();
    return sponsors
      .filter(s => {
        const created = s.created_at || s.createdAt;
        if (!created) return false;
        try { return new Date(created).toDateString() === today; } catch { return false; }
      })
      .sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt));
  }, [sponsors]);

  const handleToggleCardAdv = async (userId, current) => {
    setTogglingCardAdv(prev => new Set(prev).add(userId));
    try {
      const token = localStorage.getItem('token');
      await api.patch(
        `/opticians/sponsors/${userId}/card-advantage`,
        { enabled: !current },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast.success(current ? 'Carte avantage désactivée' : 'Carte avantage activée');
      if (fetchSponsors) await fetchSponsors();
    } catch {
      showToast.error('Erreur lors de la mise à jour');
    }
    setTogglingCardAdv(prev => { const n = new Set(prev); n.delete(userId); return n; });
  };

  const selectClient = (c) => { setSelectedClient(c); setDrawerOpen(true); markAsRead(c.id); };

  // "A traiter" badges — with explicit descriptions
  const todoItems = [
    pendingCount > 0 && { label: `${pendingCount} recommandation${pendingCount > 1 ? 's' : ''} à valider`, color: '#F59E0B', onClick: () => navigate('/optician/ongoing') },
  ].filter(Boolean);

  // Blocks for the grid
  const blocks = [
    { key: 'ongoing', icon: FaHandshake, label: 'Recommandations en cours', sub: pendingCount > 0 ? `${pendingCount} à valider` : 'Aucune en attente', stat: ongoing.length, alert: pendingCount, color: '#F59E0B', onClick: () => navigate('/optician/ongoing') },
    { key: 'completed', icon: FaCheckCircle, label: 'Recommandations traitées', stat: completed.length, color: '#10B981', onClick: () => navigate('/optician/completed') },
    { key: 'sponsors', icon: FaUsers, label: 'Utilisateurs', sub: `${sponsors.length} inscrits sur l'app`, stat: sponsors.length, color: brand, onClick: () => navigate('/optician/sponsors') },
  ].filter(Boolean);

  return (
    <ProLayout title={null}>
      {/* Header banner with date + todo + notifications */}
      <div className="px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            {brandLogo && (
              <img
                src={brandLogo}
                alt=""
                className="w-10 h-10 rounded-lg object-contain bg-white border border-gray-200"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <div>
              <p className="text-[0.6875rem] text-gray-400 uppercase tracking-wider">{new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
              <h2 className="text-base font-bold text-gray-900">Bonjour{brandName ? `, ${brandName}` : currentUser?.first_name ? `, ${currentUser.first_name}` : ''}</h2>
            </div>
          </div>
          <div className="flex items-center text-gray-600">
            <NotificationCenter unreadMessages={unreadMessages} clients={clients} vouchers={vouchers} onNotificationClick={(id) => { const c = clients.find(x => x.id === id); if (c) selectClient(c); }} currentUser={currentUser} />
          </div>
        </div>
        {todoItems.length > 0 ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[0.6875rem] font-semibold text-gray-500">Actions en attente :</span>
            {todoItems.map((t, i) => (
              <button key={i} onClick={t.onClick} className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[0.6875rem] font-semibold text-white transition-all hover:scale-105" style={{ backgroundColor: t.color }}>
                <span className="w-1.5 h-1.5 rounded-full bg-white/50 animate-pulse" /> {t.label}
              </button>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <FaCheck className="w-3 h-3 text-green-500" />
            <span className="text-[0.75rem] font-medium text-green-600">Aucune action en attente — tout est à jour</span>
          </div>
        )}
      </div>

      {/* Quick actions — gros boutons cartes */}
      {(() => {
        const actions = [
          { show: true, key: 'invite', label: 'Inviter un client', sub: 'Envoyer une invitation', icon: FaUserPlus, onClick: () => navigate('/optician/invite') },
          { show: isOptionActive('order-status'), key: 'order', label: 'Commande prête', sub: 'Prévenir le client', icon: FaBoxOpen, onClick: () => navigate('/optician/order-notify') },
          { show: isOptionActive('feed'), key: 'feed', label: 'Mes actus', sub: 'Publier une actu', icon: FaPen, onClick: () => navigate('/optician/publish') },
          { show: !!currentUser?.sms_feature_enabled, key: 'sms', label: 'Envoyer un SMS', sub: 'Campagne ou message', icon: FaSms, onClick: () => navigate('/optician/sms') },
          { show: currentUser?.vouchers_enabled !== false, key: 'vouchers', label: 'Bons d\'achat', sub: 'Gérer les bons', icon: FaGift, onClick: () => navigate('/optician/vouchers') },
          { show: !!currentUser?.glady_gift_card_enabled, key: 'gift', label: 'Carte cadeau', sub: 'Acheter / recharger', icon: FaGift, onClick: () => navigate('/optician/gift-card') },
          { show: isOptionActive('card-advantage'), key: 'card', label: 'Carte avantage', sub: 'Avantages clients', icon: FaCreditCard, onClick: () => navigate('/optician/card') },
        ].filter(a => a.show);

        return (
          <div className="px-5 py-2 flex-shrink-0">
            <div className="flex items-stretch gap-2 overflow-x-auto">
              {actions.map(a => (
                <button
                  key={a.key}
                  onClick={a.onClick}
                  title={a.sub}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 flex-shrink-0"
                >
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: brand + '15' }}
                  >
                    <a.icon className="w-3.5 h-3.5" style={{ color: brand }} />
                  </div>
                  <span className="text-[0.75rem] font-semibold text-gray-900 whitespace-nowrap">{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Grid + Panel */}
      <div className="flex-1 p-4 overflow-y-auto min-h-0">
        <div className="flex flex-col gap-3">
          {/* Core blocks (compact) */}
          <div className="grid gap-2.5 grid-cols-3">
            {blocks.filter(b => ['ongoing', 'completed', 'sponsors'].includes(b.key)).map(b => (
              <button key={b.key} onClick={b.onClick}
                className="group relative rounded-xl text-left transition-all hover:-translate-y-0.5 p-3 border bg-white border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: b.color + '18' }}>
                      <b.icon className="w-4 h-4" style={{ color: b.color }} />
                    </div>
                    {b.alert > 0 && (
                      <div
                        className="absolute -top-1 -right-1 min-w-[1.125rem] h-[1.125rem] rounded-full flex items-center justify-center px-1 ring-2 ring-white"
                        style={{ backgroundColor: b.color }}
                      >
                        <span className="text-[0.625rem] font-bold text-white leading-none">{b.alert}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[0.75rem] font-semibold text-gray-700 truncate">{b.label}</p>
                    {b.sub && <p className="text-[0.625rem] text-gray-400 truncate">{b.sub}</p>}
                  </div>
                  <p className="text-2xl font-bold text-gray-900 tabular-nums">{b.stat}</p>
                  <FaArrowRight className="w-3 h-3 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
                </div>
              </button>
            ))}
          </div>
          {/* Option blocks */}
          {blocks.filter(b => !['ongoing', 'completed', 'sponsors'].includes(b.key)).length > 0 && (
            <div className="grid gap-2.5 grid-cols-4">
              {blocks.filter(b => !['ongoing', 'completed', 'sponsors'].includes(b.key)).map(b => (
                <button key={b.key} onClick={b.onClick}
                  className="group relative rounded-xl text-left transition-all hover:-translate-y-0.5 p-3 border bg-white border-gray-200">
                  {b.alert > 0 && (
                    <div className="absolute top-2 right-2 min-w-[1.25rem] h-[1.25rem] rounded-full flex items-center justify-center px-1" style={{ backgroundColor: b.color }}>
                      <span className="text-[0.625rem] font-bold text-white">{b.alert}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: b.color + '18' }}>
                      <b.icon className="w-4 h-4" style={{ color: b.color }} />
                    </div>
                    <p className="text-xl font-bold text-gray-900">{b.stat}</p>
                  </div>
                  <p className="text-[0.6875rem] font-medium text-gray-600">{b.label}</p>
                  {b.sub && <p className="text-[0.625rem] text-gray-400 mt-0.5">{b.sub}</p>}
                </button>
              ))}
            </div>
          )}

          {/* ─── Recommandations du jour + Utilisateurs du jour + Agenda du jour ─── */}
          <div className={`mt-5 grid grid-cols-1 gap-3 ${isOptionActive('appointment') ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: brand + '12' }}>
                  <FaHandshake className="w-3.5 h-3.5" style={{ color: brand }} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Recommandations du jour</h3>
                  <p className="text-[0.6875rem] text-gray-400">
                    {todayRecos.length === 0 ? 'Aucune recommandation aujourd\'hui' : `${todayRecos.length} reçue${todayRecos.length > 1 ? 's' : ''} aujourd'hui`}
                  </p>
                </div>
              </div>
              {todayRecos.length > 0 && (
                <button
                  onClick={() => navigate('/optician/ongoing')}
                  className="text-[0.6875rem] font-semibold hover:underline"
                  style={{ color: brand }}
                >
                  Voir toutes
                </button>
              )}
            </div>
            {todayRecos.length === 0 ? (
              <div className="py-10 text-center">
                <FaHandshake className="w-6 h-6 text-gray-200 mx-auto mb-2" />
                <p className="text-[0.75rem] text-gray-400">Les nouvelles recommandations apparaîtront ici</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {todayRecos.slice(0, 6).map(c => {
                  const time = new Date(c.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                  const statusBadge = (status) => {
                    const map = {
                      pending: { label: 'En attente', cls: 'bg-amber-50 text-amber-600' },
                      refund: { label: 'En cours', cls: 'bg-blue-50 text-blue-600' },
                      payed: { label: 'Payé', cls: 'bg-purple-50 text-purple-600' },
                      approved: { label: 'Approuvé', cls: 'bg-green-50 text-green-600' },
                      delivered: { label: 'Livré', cls: 'bg-emerald-50 text-emerald-600' },
                      rejected: { label: 'Rejeté', cls: 'bg-red-50 text-red-600' },
                    };
                    const s = map[status] || { label: status, cls: 'bg-gray-50 text-gray-600' };
                    return <span className={`text-[0.625rem] font-medium px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
                  };
                  return (
                    <div
                      key={c.id}
                      onClick={() => navigate('/optician/ongoing')}
                      className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[0.6875rem] font-bold flex-shrink-0"
                          style={{ backgroundColor: brand }}
                        >
                          {(c.first_name || '?')[0]}{(c.last_name || '?')[0]}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900 truncate">{c.first_name} {c.last_name}</p>
                          <p className="text-[0.6875rem] text-gray-400">
                            Recommandé par <strong className="text-gray-600">{c.sponsor_name || 'Inconnu'}</strong> — {time}
                          </p>
                        </div>
                      </div>
                      {statusBadge(c.status)}
                    </div>
                  );
                })}
                {todayRecos.length > 6 && (
                  <button
                    onClick={() => navigate('/optician/ongoing')}
                    className="w-full py-2.5 text-[0.6875rem] font-semibold bg-gray-50 hover:bg-gray-100 transition-colors"
                    style={{ color: brand }}
                  >
                    + {todayRecos.length - 6} autre{todayRecos.length - 6 > 1 ? 's' : ''}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* ─── Utilisateurs du jour ─── */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: brand + '12' }}>
                  <FaUsers className="w-3.5 h-3.5" style={{ color: brand }} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Utilisateurs du jour</h3>
                  <p className="text-[0.6875rem] text-gray-400">
                    {todayUsers.length === 0 ? 'Aucune inscription aujourd\'hui' : `${todayUsers.length} inscription${todayUsers.length > 1 ? 's' : ''} aujourd'hui`}
                  </p>
                </div>
              </div>
              {todayUsers.length > 0 && (
                <button
                  onClick={() => navigate('/optician/sponsors')}
                  className="text-[0.6875rem] font-semibold hover:underline"
                  style={{ color: brand }}
                >
                  Voir tous
                </button>
              )}
            </div>
            {todayUsers.length === 0 ? (
              <div className="py-10 text-center">
                <FaUsers className="w-6 h-6 text-gray-200 mx-auto mb-2" />
                <p className="text-[0.75rem] text-gray-400">Les nouveaux inscrits apparaîtront ici</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {todayUsers.slice(0, 6).map(s => {
                  const created = s.created_at || s.createdAt;
                  const time = created ? new Date(created).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
                  const enabled = !!s.cardAdvantageEnabled;
                  const busy = togglingCardAdv.has(s.id);
                  return (
                    <div key={s.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[0.6875rem] font-bold flex-shrink-0"
                          style={{ backgroundColor: brand }}
                        >
                          {(s.first_name || '?')[0]}{(s.last_name || '?')[0]}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900 truncate">{s.first_name} {s.last_name}</p>
                          <p className="text-[0.6875rem] text-gray-400 truncate">
                            {s.email || s.phone || 'Inscrit'}{time && ` — ${time}`}
                          </p>
                        </div>
                      </div>
                      {isOptionActive('card-advantage') && (
                        <button
                          onClick={() => handleToggleCardAdv(s.id, enabled)}
                          disabled={busy}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[0.6875rem] font-semibold transition-colors disabled:opacity-50 flex-shrink-0"
                          style={enabled
                            ? { backgroundColor: brand + '15', color: brand }
                            : { backgroundColor: brand, color: '#fff' }
                          }
                          title={enabled ? 'Carte avantage active — cliquez pour désactiver' : 'Activer la carte avantage pour ce client'}
                        >
                          <FaIdCard className="w-3 h-3" />
                          {enabled ? 'Carte avantage activée' : 'Activer la carte avantage'}
                        </button>
                      )}
                    </div>
                  );
                })}
                {todayUsers.length > 6 && (
                  <button
                    onClick={() => navigate('/optician/sponsors')}
                    className="w-full py-2.5 text-[0.6875rem] font-semibold bg-gray-50 hover:bg-gray-100 transition-colors"
                    style={{ color: brand }}
                  >
                    + {todayUsers.length - 6} autre{todayUsers.length - 6 > 1 ? 's' : ''}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* ─── Agenda du jour (inline) ─── */}
          {isOptionActive('appointment') && (() => {
            const todayStr = new Date().toDateString();
            const rdvsToday = rdvList
              .filter(r => { try { return new Date(r.date).toDateString() === todayStr; } catch { return false; } })
              .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));

            return (
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#3B82F612' }}>
                      <FaCalendarAlt className="w-3.5 h-3.5 text-blue-500" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">Agenda du jour</h3>
                      <p className="text-[0.6875rem] text-gray-400">
                        {rdvsToday.length === 0 ? 'Aucun rendez-vous aujourd\'hui' : `${rdvsToday.length} rendez-vous`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/optician/rdv')}
                    className="text-[0.6875rem] font-semibold hover:underline"
                    style={{ color: brand }}
                  >
                    Voir tout
                  </button>
                </div>
                {rdvsToday.length === 0 ? (
                  <div className="py-10 text-center">
                    <FaCalendar className="w-6 h-6 text-gray-200 mx-auto mb-2" />
                    <p className="text-[0.75rem] text-gray-400">Journée calme</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {rdvsToday.slice(0, 6).map(r => {
                      const time = (r.start_time || '').slice(0, 5) || '—';
                      const name = `${r.user?.first_name || ''} ${r.user?.last_name || ''}`.trim() || 'Client';
                      return (
                        <div key={r.id} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors">
                          <div className="flex-shrink-0 w-12 text-center">
                            <p className="text-sm font-bold text-gray-900 tabular-nums">{time}</p>
                          </div>
                          <div className="w-px h-8 bg-gray-200 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
                            {r.reason && <p className="text-[0.6875rem] text-gray-400 truncate">{r.reason}</p>}
                          </div>
                        </div>
                      );
                    })}
                    {rdvsToday.length > 6 && (
                      <button
                        onClick={() => navigate('/optician/rdv')}
                        className="w-full py-2.5 text-[0.6875rem] font-semibold bg-gray-50 hover:bg-gray-100 transition-colors"
                        style={{ color: brand }}
                      >
                        + {rdvsToday.length - 6} autre{rdvsToday.length - 6 > 1 ? 's' : ''}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          </div>
        </div>
      </div>

      {/* MessageDrawer is the only overlay kept */}
      <MessageDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} client={selectedClient} messages={messages.filter(m => m.client_id === selectedClient?.id)} currentUser={currentUser}
        onSendMessage={async (msg) => { const sent = await sendMessage(msg); if (sent) fetchMessages(true, clients); return !!sent; }} />
      <Toaster />
    </ProLayout>
  );
};

export default DashboardPage;
